<template>
  <BModal
    :modal-name="modalName"
    height="auto"
  >
    <BHeightAdjuster>
      <BModalHeader
        :title="title"
        center
        @modal-close="hide"
      />
      <BModalBody>
        <BLayout
          justify-center
          align-center
          is-fit
        >
          <BFileUploadArea
            ref="fileUploadArea"
            class="mt-400 file-upload-area"
            :accept-data-type="acceptDataType"
            is-fit
            @upload="upload"
          />
        </BLayout>
      </BModalBody>
      <BModalFooter>
        <BLayout justify-center>
          <div class="message">
            <span class="desc">{{ templateDesc }}</span>
            <div class="dl-btn">
              <BBtn
                type="primary"
                @click="dlButton"
              >
                <span>{{ downloadLabel }}</span>
              </BBtn>
            </div>
            <BBtn
              :loading="loading"
              :disabled="!save"
              type="primary"
              @click="saveButton"
            >
              <span>{{ importLabel }}</span>
            </BBtn>
          </div>
        </BLayout>
      </BModalFooter>
    </BHeightAdjuster>
  </BModal>
</template>

<script lang="ts" setup>
import type { AxiosError, AxiosResponse } from 'axios';
import { createApp, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ExcelImportErrorContent from '@/components/organisms/user/general/modal/ExcelImportErrorContent.vue';
import { useAlert, useAlertWithContent } from '@/composable/message-dialog';
import { KEY_OF_MODAL } from '@/injection-keys';
import { downloadExcel } from '@/utils/file';

type TProps = {
  modalName: string;
  title: string;
  templateDesc: string;
  downloadLabel: string;
  importLabel: string;
  templateFileName: string;
  // インポート本体（API呼び出し）。ファイル形式の差異（FormData / request:{file}）や
  // errorHandlers の指定は呼び出し側のクロージャに閉じ込める。
  onImport: (file: File) => Promise<unknown>;
  // テンプレートDL。blob を持つレスポンスを返す。
  onDownloadTemplate: () => Promise<AxiosResponse<Blob>>;
  acceptDataType?: string;
  maxFileSize?: number;
};

const props = withDefaults(defineProps<TProps>(), {
  acceptDataType: '.xlsx',
  maxFileSize: 100000,
});

const emit = defineEmits<TEmit>();
type TEmit = (event: 'refresh') => void;
const i18n = useI18n();
const { alert } = useAlert();
const { alertWithContent } = useAlertWithContent();
const modal = inject(KEY_OF_MODAL);
const loading = ref(false);
const save = ref(false);
const selectedFile = ref<File | null>(null);
const fileUploadArea = ref<{ clear: () => void }>();

const upload = async (file: File) => {
  loading.value = true;
  const separatedFileName = file.name.split('.');
  const importFileExtension = `.${separatedFileName[separatedFileName.length - 1]}`;

  if (importFileExtension.toLowerCase() !== props.acceptDataType) {
    onFileUploadFailed();
    await alert({ message: i18n.t('import.errors.extension') });
    return;
  }
  if (file.size > props.maxFileSize) {
    onFileUploadFailed();
    await alert({ message: i18n.t('import.errors.maxFileSize', { max_file_size: `${props.maxFileSize / 1000}KB` }) });
    return;
  }

  selectedFile.value = file;
  save.value = true;
  loading.value = false;
};

const saveButton = async () => {
  if (!selectedFile.value) {
    await alert({ message: i18n.t('apiError.message.fileUploadError') });
    loading.value = false;
    save.value = false;
    return;
  }
  try {
    await props.onImport(selectedFile.value);
    save.value = false;
    loading.value = false;
    emit('refresh');
    hide();
  } catch (err) {
    const axiosError = err as AxiosError<{ errorMessage?: string }>;
    const errorMessage = axiosError.response?.data?.errorMessage ?? '';
    const container = document.createElement('div');
    const app = createApp(ExcelImportErrorContent, { errorMessage });
    app.mount(container);
    try {
      await alertWithContent({ title: i18n.t('import.excel.errorTitle'), content: container });
    } finally {
      app.unmount();
    }
    loading.value = false;
  }
};

const dlButton = async () => {
  const res = await props.onDownloadTemplate();
  downloadExcel(res.data, props.templateFileName);
};

const onFileUploadFailed = () => {
  selectedFile.value = null;
  save.value = false;
  loading.value = false;
  fileUploadArea.value?.clear();
};

const hide = () => {
  selectedFile.value = null;
  save.value = false;
  fileUploadArea.value?.clear();
  modal?.hide(props.modalName);
};
</script>

<style lang="scss" scoped>
.message {
  padding: $basespace-100 0;
  text-align: center;
  color: $basecolor-primary;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  .desc {
    padding-bottom: $basespace-200;
    color: $concrete-dark;
  }
  .dl-btn {
    padding-bottom: $basespace-500;
  }
}
.file-upload-area {
  width: 100%;
}
</style>
